/**
 * Google Sign In — Firebase Auth 기반
 *
 * 웹/PWA: Firebase signInWithPopup (GoogleAuthProvider)
 * Android 네이티브: Credential Manager → Google ID Token → Firebase credential
 * iOS 네이티브: NativeGoogleLoginPlugin(GIDSignIn) → ID Token
 *              → GoogleAuthProvider.credential(idToken) → signInWithCredential
 */

import { GoogleAuthProvider, getAdditionalUserInfo, signInWithPopup, signInWithCredential } from 'firebase/auth'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { auth } from './firebase'

const NativeGoogleLogin = registerPlugin('NativeGoogleLogin')

const GOOGLE_ERROR_MESSAGES = {
  'auth/unauthorized-domain':                         '현재 도메인이 Firebase 인증 허용 도메인에 등록되어 있지 않습니다. Firebase Console > Authentication > Settings > Authorized domains에 추가해주세요.',
  'auth/operation-not-allowed':                       'Firebase에서 Google 로그인이 활성화되지 않았습니다. Authentication > Sign-in method에서 Google을 활성화해주세요.',
  'auth/popup-blocked':                               '브라우저가 Google 로그인 팝업을 차단했습니다. 팝업 허용 후 다시 시도해주세요.',
  'auth/popup-closed-by-user':                        'Google 로그인이 취소되었습니다.',
  'auth/cancelled-popup-request':                     'Google 로그인이 취소되었습니다.',
  'auth/not-initialized':                             'Firebase 인증 설정을 확인해주세요.',
  'auth/operation-not-supported-in-this-environment': '이 환경에서는 Google 로그인을 지원하지 않습니다.',
}

function toUserFacingGoogleError(error) {
  console.error('[GoogleAuth] sign-in failed', { code: error?.code ?? null, message: error?.message ?? String(error) })
  const msg  = GOOGLE_ERROR_MESSAGES[error?.code] || 'Google 로그인에 실패했습니다. 잠시 후 다시 시도해주세요.'
  const next = new Error(msg)
  next.code          = error?.code
  next.originalError = error
  return next
}

function normalizeNativeError(err) {
  const code      = err?.code || 'LOGIN_FAILED'
  const cancelled = err?.data?.cancelled === true || code === 'CANCELLED'
  const message   = err?.message || 'Google 로그인에 실패했습니다.'
  const next      = new Error(message)
  next.code      = code
  next.cancelled = cancelled
  return next
}

/**
 * Google 로그인.
 * Android: Credential Manager → ID Token → Firebase credential
 * iOS 네이티브: GIDSignIn → ID Token → Firebase credential
 * 웹: signInWithPopup
 * @returns {{ id, uid, provider, displayName, email, photoURL }}
 */
export async function loginWithGoogle() {
  if (!auth) {
    const e = new Error('Firebase Auth가 초기화되지 않았습니다. VITE_FIREBASE_* 환경변수를 확인하세요.')
    e.code = 'auth/not-initialized'
    throw e
  }

  if (Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())) {
    // ── Android/iOS 네이티브 경로 ────────────────────────────────────────────────────
    let nativeResult
    try {
      nativeResult = await NativeGoogleLogin.login()
    } catch (err) {
      throw normalizeNativeError(err)
    }

    const { idToken } = nativeResult ?? {}
    if (!idToken) {
      const e = new Error('Google ID Token을 받지 못했습니다.')
      e.code = 'TOKEN_MISSING'
      throw e
    }

    try {
      const credential = GoogleAuthProvider.credential(idToken)
      const result     = await signInWithCredential(auth, credential)
      const fbUser     = result.user
      return {
        id:          fbUser.uid,
        uid:         fbUser.uid,
        provider:    'google',
        displayName: fbUser.displayName || 'Google 사용자',
        email:       fbUser.email       || null,
        photoURL:    fbUser.photoURL    || null,
        isNewUser:   getAdditionalUserInfo(result)?.isNewUser === true,
      }
    } catch (error) {
      throw toUserFacingGoogleError(error)
    }
  }

  // ── 웹/PWA 경로 ─────────────────────────────────────────────────────────────
  try {
    const provider = new GoogleAuthProvider()
    provider.addScope('email')
    provider.addScope('profile')

    const result = await signInWithPopup(auth, provider)
    const fbUser = result.user

    return {
      id:          fbUser.uid,
      uid:         fbUser.uid,
      provider:    'google',
      displayName: fbUser.displayName || 'Google 사용자',
      email:       fbUser.email       || null,
      photoURL:    fbUser.photoURL    || null,
      isNewUser:   getAdditionalUserInfo(result)?.isNewUser === true,
    }
  } catch (error) {
    throw toUserFacingGoogleError(error)
  }
}

/** Android/iOS 네이티브 Google 세션 정리. 웹은 Firebase signOut만으로 충분. */
export async function logoutFromGoogle() {
  if (Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())) {
    try { await NativeGoogleLogin.logout() } catch { /* 세션 정리 실패가 전체 로그아웃을 막으면 안 됨 */ }
  }
}
